/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import _ from "underscore";
import {
    useExternalGender,
    useSelectedGender,
} from "web/react/components/feeds-filters/hooks/use-external-gender";
import { PendingFilters } from "web/react/components/feeds-filters/pending-filters";
import { useDesktopBreakpoint } from "web/react/hooks/use-desktop-breakpoint/use-desktop-breakpoint";
import { useHistoryWatcher } from "web/react/hooks/use-history-watcher/use-history-watcher";
import { hideLandingContent, setTitle } from "web/redux/ducks/feed-filters";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import rothko from "web/script/modules/rothko";
import { getURL } from "web/script/routing/getUrl";
import { getProductPageRegex } from "web/script/routing/routes";
import history from "web/script/utils/history";
import logging from "web/script/utils/logging";
import { AllSelectedFilterInfo } from "web/types/feed-filters";
import {
    FilteredScrollAreaFilterOptionGroup,
    UniversalFilterOption,
    UniversalFiltersData,
} from "web/types/universal-filters";
import { useDualStates } from "./use-dual-states";
import { getDefaultSelectOption, useFilterClickHandler } from "./use-filter-click-handler";
import { useFilterCountHandler } from "./use-filter-count-handler";
import { useRothkoFetcher } from "./use-rothko-fetcher";
import { useSelectedFilterInfo } from "./use-selected-filter-info";
import { useSortingHandler } from "./use-sorting-handler";

/**
 * Find the most deeply nested selected item with no selected siblings
 */
function findLeafSelection(filters: UniversalFilterOption[]): UniversalFilterOption | null {
    let selectedChildren = filters.filter((item) => item.checked);
    if (selectedChildren.length != 1) {
        return null;
    }
    let filter = selectedChildren[0];
    if (filter.type == "category") {
        return findLeafSelection(filter.children) || filter;
    } else {
        return filter;
    }
}

export function getFeedsTitle(filters: UniversalFiltersData): string {
    let selectedLeaves = {};
    for (let section of filters.filter_section_list) {
        if (section.type != "pair_of_selects") {
            selectedLeaves[section.id] = findLeafSelection(section.option_groups[0].options);
        }
    }

    let selectedGender = selectedLeaves["section-gender"];
    let selectedGenderValue = selectedGender && selectedGender.value;
    let selectedTaxonomy = selectedLeaves["section-category"];
    let selectedDesigner = selectedLeaves["section-designer-slug"];
    let selectedColor = selectedLeaves["section-color"];
    let selectedMaterial = selectedLeaves["section-material"];

    const forGenderLabels = {
        Women: gettext("general.womens"),
        Men: gettext("general.mens"),
    };

    let forGender = forGenderLabels[selectedGenderValue] || gettext("general.all");

    if (!selectedTaxonomy) {
        if (selectedDesigner) {
            // eslint-disable-next-line max-depth
            if (selectedGender) {
                return gettext("designer_shop.designer_gender_page_title", {
                    for_gender: forGender,
                    designer_name: selectedDesigner.label,
                });
            }
            return selectedDesigner.label;
        }
        return gettext("feed.browse_title");
    }
    /* eslint-disable max-depth */
    if (selectedDesigner) {
        if (selectedGenderValue !== "all") {
            if (selectedTaxonomy.name == "product_type") {
                return gettext("product.breadcrumbs.type_by_designer_for_gender", {
                    for_gender: forGender,
                    product_type: selectedTaxonomy.label,
                    designer_name: selectedDesigner.label,
                });
            }
            if (selectedTaxonomy.name == "category") {
                return gettext("product.breadcrumbs.category_by_designer_for_gender", {
                    for_gender: forGender,
                    product_category: selectedTaxonomy.label,
                    designer_name: selectedDesigner.label,
                });
            }
            if (selectedTaxonomy.name == "subcategory") {
                return gettext("product.breadcrumbs.subcategory_by_designer_for_gender", {
                    for_gender: forGender,
                    product_subcategory: selectedTaxonomy.label,
                    designer_name: selectedDesigner.label,
                });
            }
        } else {
            if (selectedTaxonomy.name == "product_type") {
                return gettext("product.breadcrumbs.type_by_designer", {
                    product_type: selectedTaxonomy.label,
                    designer_name: selectedDesigner.label,
                });
            }
            if (selectedTaxonomy.name == "category") {
                return gettext("product.breadcrumbs.category_by_designer", {
                    product_category: selectedTaxonomy.label,
                    designer_name: selectedDesigner.label,
                });
            }
            if (selectedTaxonomy.name == "subcategory") {
                return gettext("product.breadcrumbs.subcategory_by_designer", {
                    product_subcategory: selectedTaxonomy.label,
                    designer_name: selectedDesigner.label,
                });
            }
        }
    } else {
        if (selectedColor && !selectedMaterial) {
            return gettext("feed.header.template_color", {
                for_gender: forGender,
                color: selectedColor.label,
                category_name: selectedTaxonomy.label,
            });
        }
        if (selectedMaterial && !selectedColor) {
            return gettext("feed.header.template_material", {
                for_gender: forGender,
                material: selectedMaterial.label,
                category_name: selectedTaxonomy.label,
            });
        }
        if (selectedTaxonomy.name == "product_type") {
            return gettext("product.breadcrumbs.type_for_gender", {
                product_type: selectedTaxonomy.label,
                for_gender: forGender,
            });
        }
        if (selectedTaxonomy.name == "category") {
            return gettext("product.breadcrumbs.category_for_gender", {
                product_category: selectedTaxonomy.label,
                for_gender: forGender,
            });
        }
        if (selectedTaxonomy.name == "subcategory") {
            return gettext("product.breadcrumbs.subcategory_for_gender", {
                product_subcategory: selectedTaxonomy.label,
                for_gender: forGender,
            });
        }
    }
    /* eslint-enable max-depth */

    return gettext("feed.browse_title");
}

export interface FilterManager {
    applyState(): void;
    canApply: boolean;
    cancelState(): void;
    displayedFilters: UniversalFiltersData;
    isLoading: boolean;
    lazyFacetFetch(optionGroupId: string): void;
    lazyFacetIsLoading: boolean;
    onClick(filter: UniversalFilterOption, immediateUnselect?, alwaysApply?: boolean): void;
    pendingFilterInfo: PendingFilters;
    selectedFilterInfo: AllSelectedFilterInfo;
    updateFromServer(): void;
}

export function useFilterManager(initialState: UniversalFiltersData): FilterManager {
    // This is the manager for having unapplied and applied filters
    const {
        activeFilters,
        applyDisplayedFilters,
        displayedFilters,
        revertDisplayedFilters,
        setDisplayedFilters,
    } = useDualStates(initialState);

    const {
        toggleFilter,
        // This is an override object for `displayedFilters` so the user can click
        //  on things without having to wait for rothko after every tap
        pendingFilterInfo,
        resetPendingFilters,
    } = useFilterClickHandler(displayedFilters);

    // This is the current sorting system the user has chosen.
    // I'm not going to include it in any of our state to avoid having to deal
    //  with what happens when it updates - it can just stay in Redux and be
    //  pulled out whenever we update the URL
    const feedSortingState = useSortingHandler();
    // This is all the info required to display the selected filter state on the
    //  menu, with labels, selected subcounts & breadcrumb pills
    const [numSelectedFilters, selectedFilterInfo] = useSelectedFilterInfo(
        displayedFilters,
        pendingFilterInfo
    );

    const onFiltersUpdated = useCallback(
        (newState: UniversalFiltersData) => {
            setDisplayedFilters(newState);
            resetPendingFilters();
        },
        [resetPendingFilters, setDisplayedFilters]
    );

    const [fetchFilters, isLoading] = useRothkoFetcher<UniversalFiltersData>(
        "modules/universal_filters",
        onFiltersUpdated
    );

    // fetch facets for designers or retailers
    const [lazyFiltersFetch, lazyFacetIsLoading] = useRothkoFetcher<
        FilteredScrollAreaFilterOptionGroup
    >("modules/option_group_filtered_scroll_area");

    useFilterCountHandler(numSelectedFilters);

    const hasSidebar = useDesktopBreakpoint();
    const dispatch = useDispatch();
    const urlData = useHistoryWatcher();

    /*
     * SERVER TALKIN'
     */
    function generateFeedsURL(newFilters: UniversalFiltersData) {
        return new URL(newFilters.canonical_url, window.location.href);
    }

    function updateFeedsQuery(url: URL) {
        if (feedSortingState) {
            if (feedSortingState == "recommended") {
                // Don't include the default sorting in the URL
                url.searchParams.delete("view");
            } else {
                url.searchParams.set("view", feedSortingState);
            }
        }

        history.pushState(url);
    }

    function setFeedsTitle(filters: UniversalFiltersData) {
        let title = getFeedsTitle(filters);
        dispatch(setTitle(title));
    }

    async function refreshFromServer(
        query: URLSearchParams
    ): Promise<UniversalFiltersData | undefined> {
        try {
            // The query order _shouldn't_ matter, but in case it does be consistent
            query.sort();
            return await fetchFilters(query);
        } catch (e) {
            // TODO: I don't know what to do in this case? Retry? Discard all pending filters?
            logging.error(e, "TODO");
        }
    }

    async function lazyFacetFetch(optionGroupId: string) {
        let params = new URLSearchParams(displayedFilters.query_string);
        params.set("option_group_id", optionGroupId);
        let data;
        try {
            data = await lazyFiltersFetch(params);
        } catch (err) {
            logging.error(err, `Failed to fetch optitons for ${optionGroupId}`);
            return;
        }
        // Find the right place in the displayedFilters to insert this. It
        // should replace the data for the option group we just fetched

        // Note that re-fetching the main set of facets (refreshFromServer)
        // resets the lazy loaded facets. This is what we want for now, since
        // it will force us to re-fetch them when next opened.

        // we clone the whole displayedFilters so we can mutate our copy in-place
        let newFilterSectionList = _.clone(displayedFilters.filter_section_list);
        for (let section of newFilterSectionList) {
            // this only works for single-group sections
            if (section.option_groups.length != 1) {
                continue;
            }
            let group = section.option_groups[0];
            if (group.id != optionGroupId) {
                continue;
            }
            section.option_groups = [data];
            setDisplayedFilters({
                ...displayedFilters,
                filter_section_list: newFilterSectionList,
            });
            return;
        }
        logging.error(`${optionGroupId} does not exist in the displayed filters!`);
    }

    /*
     *  CALLBACKS
     */
    async function applyState(newFilters?: UniversalFiltersData): Promise<void> {
        if (!newFilters && pendingFilterInfo.hasPending()) {
            // TODO: This call may fail in which case we'll just apply all the
            //        non-pending filters
            newFilters = await refreshFromServer(pendingFilterInfo.toQuery(displayedFilters));
        }
        // TODO: Check if there are actually any changes to be applied
        applyDisplayedFilters(newFilters);

        // send the event as part of the current page view
        analytics.event("filter", "apply");
        updateFeedsQuery(generateFeedsURL(newFilters || displayedFilters));
        if (displayedFilters.feed_url == getURL("exploreFeed")) {
            // if the feed_url isn't /explore/, we have pre-filters that we can't remove, which
            // are also what's powering e.g. the title and description
            setFeedsTitle(newFilters || displayedFilters);
            dispatch(hideLandingContent());
        }

        let topElement = document.querySelector(".product-feed--top");
        // Scroll to the top on so users can see all the new products
        if (topElement && topElement instanceof HTMLElement && hasSidebar) {
            let fourCardLayout = environment.getFeature("uj_four_product_feed");
            let newBackgroundColors = environment.getFeature("uj_background_color_switch");

            // offset to scroll a little bit above the feed
            let scrollOffset = 30;

            if (fourCardLayout && newBackgroundColors) {
                scrollOffset = 65;
            }

            let currentPosition = document.documentElement.scrollTop;
            let feedTopPosition = topElement.getBoundingClientRect().top - scrollOffset;

            if (feedTopPosition < 0) {
                let scrollPos = Math.round(feedTopPosition + currentPosition);
                window.scrollTo(0, scrollPos);
            }
        } else {
            window.scrollTo(0, 0);
        }
        analytics.event("filter", "apply");
    }

    /**
     * Universal handler for filtery things being clicked on
     * @param filter The filter item the user clicked on
     * @param immediateUnselect If they clicked it in the breadcrumb/pill thingy
     * @param alwaysApply If we want to override sidebar behaviour when the action is from gender switch
     */
    async function onClick(
        filter: UniversalFilterOption,
        immediateUnselect = false,
        alwaysApply = false
    ): Promise<void> {
        if (isLoading) {
            return;
        }

        if (immediateUnselect && filter.type == "option") {
            // We need to change this to a request to select the default value
            //  for this option group because you can't "unselect" options.
            filter = getDefaultSelectOption(filter, displayedFilters);
        }

        let pendingFilterInfo = toggleFilter(filter);

        let shouldApply = alwaysApply || hasSidebar;

        if (immediateUnselect || shouldApply) {
            let newFilters = await refreshFromServer(pendingFilterInfo.toQuery(displayedFilters));
            // Make sure the menu reflects the new filters if we're on desktop
            if (shouldApply) {
                applyState(newFilters);
            }
        }
    }

    function cancelState(): void {
        revertDisplayedFilters();
        resetPendingFilters();
        // Nuke any in-flight update requests by creating a new one.
        // This should be resolved immediately from the cache, but even if it
        //  isn't the user won't see anything because the filters will be closed.
        refreshFromServer(new URLSearchParams(activeFilters.query_string));
    }

    function updateFromServer(): void {
        if (pendingFilterInfo.hasPending()) {
            refreshFromServer(pendingFilterInfo.toQuery(displayedFilters));
        }
    }

    let canApply =
        activeFilters.canonical_url != displayedFilters.canonical_url ||
        pendingFilterInfo.hasPending();

    useEffect(() => {
        if (initialState.filter_section_list.length) {
            // Avoid re-fetching the initial state once we have it.
            rothko.addCacheEntry(
                "modules/universal_filters",
                initialState.query_string,
                initialState
            );
            return;
        }

        async function getInitialData() {
            try {
                let newFilters = await refreshFromServer(
                    new URLSearchParams(initialState.query_string)
                );
                applyDisplayedFilters(newFilters);
            } catch (err) {
                logging.error(err, "Failed to get universal_filters data");
            }
        }

        getInitialData();
        // We only ever need to run this hook once, if we are initialised with no filters from the
        // server (e.g. on /search/, where we want to fetch them with ajax after the page load)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!urlData) {
            return;
        }

        if (getProductPageRegex().test(urlData.newURL.pathname)) {
            // overlay
            return;
        }

        async function refetchFilters(searchParams: URLSearchParams) {
            let newFilters = await refreshFromServer(searchParams);
            if (!newFilters) {
                // We don't know what should be displayed on the filters, so reload the page to make sure what the user interacts with is correct.
                // This is fairly reasonable behaviour for the back button so people shouldn't notice
                window.location.reload();
                return;
            }
            applyDisplayedFilters(newFilters);

            if (newFilters.feed_url == getURL("exploreFeed")) {
                // if the feed_url isn't /explore/, we have pre-filters that we can't remove, which
                // are also what's powering the title
                setFeedsTitle(newFilters);
            }
        }

        const currentPageUrl = new URL(urlData.newURL.href);
        const canonicalUrl = new URL(activeFilters.canonical_url, window.location.href);
        if (currentPageUrl.href != canonicalUrl.href) {
            let searchParams = currentPageUrl.searchParams;
            searchParams.set("feed_url", currentPageUrl.pathname);
            refetchFilters(searchParams);
        }
        // This hook should only run when the page URL changes, so we don't care about the state of anything outside of that situation
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlData]);

    useExternalGender(activeFilters);

    const selectedGender = useSelectedGender();
    // When gender is changed from "FeedGenderSwitch" we need to treat it the same as the user clicked a filter
    useEffect(() => {
        if (selectedGender) {
            onClick(selectedGender, false, true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedGender]);

    return {
        applyState,
        canApply,
        cancelState,
        displayedFilters,
        isLoading,
        onClick,
        pendingFilterInfo,
        selectedFilterInfo,
        updateFromServer,
        lazyFacetFetch,
        lazyFacetIsLoading,
    };
}
